<template>
  <div>
    <Panel
      :pt="{
        header: {
          style: `
          -moz-transition: all .51s;
          -o-transition: all .51s;
          -webkit-transition: all .51s;
          transition: all .60s;
          background-color: var(--vd-color-neutral-light); ${
            isCollapsed ? 'border-radius: 15px' : 'border-radius: 15px 15px 0 0'
          }`,
        },
        content: {
          style: 'background-color: var(--vd-color-blue-light-9); min-height: 255px',
        },
      }"
      :collapsed="isCollapsed"
      :class="$style.cursorPointer"
      @click="isCollapsed = !isCollapsed"
    >
      <template #icons>
        <VdIcon
          :class="$style.cursorPointer"
          store="fa"
          :name="isCollapsed ? 'chevron-down' : 'chevron-up'"
          size="large"
        ></VdIcon>
      </template>
      <template #header>
        <div :class="$style.sectionHeadline">
          <VdIcon :class="$style.cursorPointer" store="fa" name="grip-lines-vertical" size="large"></VdIcon>
          <span :class="$style.sectionHeadlineText">{{ section.name }}</span>
          <span :class="$style.sectionId">{{ section.id }}</span>
          <CopyButton :id="section.id" :class="$style.copyId" @click.stop />
          <VdIcon
            v-if="props.isEditAllowed"
            :class="$style.cursorPointer"
            store="fa"
            name="edit"
            size="large"
            color="var(--vd-color-blue-default)"
            @click.stop="handleEdit"
          ></VdIcon>
          <VdIcon
            v-if="props.isEditAllowed"
            :class="$style.cursorPointer"
            store="fa"
            name="trash"
            size="large"
            color="var(--vd-color-error)"
            @click.stop="handleDelete"
          ></VdIcon>
        </div>
      </template>
      <div :class="$style.shopItemsWrapper" @click.stop>
        <draggable
          item-key="id"
          group="shop-items"
          :animation="150"
          :list="shopItems"
          :class="$style.shopItemsInnerWrapper"
          :disabled="isDraggingDisabled || !isWIPVersion"
          :move="shouldMove"
          @change="handleChange"
          @dragend="handleDragWithinSameSection"
        >
          <template #item="{ element }">
            <ShopItem
              :item="element"
              :version-id="versionId"
              @delete="handleDeleteItem"
              @edit="handleEditItem"
              @duplicate="handleDuplicateItem"
            />
          </template>
        </draggable>
        <AddShopItem v-if="isWIPVersion" @show-modal="showAddShopItemModal"></AddShopItem>
      </div>
    </Panel>
    <Dialog
      v-model:visible="displayAddShopItemModal"
      :header="shopItemToBeEdited ? 'Edit shop item' : 'Add shop item'"
      modal
      :style="{ width: '600px' }"
      dismissable-mask
      :draggable="false"
      @after-hide="resetForm"
    >
      <AddShopItemForm
        :shop-item-to-be-edited="shopItemToBeEdited"
        :section="section"
        @save-shop-item="handleAddItem"
        @update-shop-item="handleUpdateItem"
        @close="closeAddShopItemModal"
      />
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import Dialog from "primevue/dialog";
import Panel from "primevue/panel";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import draggable from "vuedraggable";

import CopyButton from "@/components/CopyButton/CopyButton.vue";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import type { CoolDown, ShopItem as ShopItemType } from "@/graphql";
import { ShopItemPriceInput } from "@/graphql";
import { ShopSection } from "@/modules/Store/shop.types";
import useShopsManager from "../hooks/useShopsManager";
import AddShopItemForm from "./AddShopItemForm.vue";
import AddShopItem from "./AddShopProduct.vue";
import ShopItem from "./ShopItem.vue";

type SortChangeType<T> = {
  added?: {
    newIndex: number;
    element: T;
  };
  removed?: {
    oldIndex: number;
    element: T;
  };
};

export type SortUpdateEvent = {
  newRank: number;
  previousRank: number;
  shopItemId: string;
  newShopSectionId: string;
  previousSectionId: string;
};

export type ShopItemAddOrRemoveEvent = {
  rank: number;
  shopItemId: string;
  sectionId: string;
};

const props = defineProps<{
  section: ShopSection;
  isEditAllowed: boolean;
  shopId: string;
}>();

const emits = defineEmits<{
  (e: "edit", sectionId: string): void;
  (e: "delete", sectionId: string): void;
  (e: "re-order-end", event: SortUpdateEvent): void;
  (e: "remove-shop-item", event: ShopItemAddOrRemoveEvent): void;
  (e: "append-shop-item", event: ShopItemAddOrRemoveEvent): void;
}>();

const route = useRoute();
const appId = route.params.gameId as string;
const versionId = route.params.versionId as string;

const shopItems = ref(props.section.items);
const isCollapsed = ref(true);
const displayAddShopItemModal = ref(false);
const isDraggingDisabled = ref(false);
const isWIPVersion = computed(() => versionId === "wip");
const shopItemToBeEdited = ref<ShopItemType | null>(null);
const { addShopItem, deleteShopItem, duplicateShopItem, updateShopItem } = useShopsManager(appId, versionId);

const showAddShopItemModal = () => {
  displayAddShopItemModal.value = true;
};

const resetForm = () => {
  shopItemToBeEdited.value = null;
};

const closeAddShopItemModal = () => {
  displayAddShopItemModal.value = false;
  resetForm();
};

const handleUpdateItem = async ({
  name,
  tags,
  rewardId,
  price,
  purchaseLimit,
  coolDown,
}: {
  name: string;
  tags: string[];
  rewardId: string;
  price: ShopItemPriceInput;
  purchaseLimit: number;
  coolDown: CoolDown;
}) => {
  if (!shopItemToBeEdited.value) {
    return;
  }
  await updateShopItem({
    id: shopItemToBeEdited.value.id || "",
    name,
    tags,
    rewardId,
    versionId,
    appId,
    price,
    rank: shopItemToBeEdited.value.rank,
    sectionId: props.section.id,
    shopId: props.shopId,
    purchaseLimit,
    coolDown,
  });
  closeAddShopItemModal();
};

watch(
  () => props.section,
  (newSection) => {
    shopItems.value = [...newSection.items];
    isDraggingDisabled.value = false;
  }
);

const handleAddItem = async ({
  name,
  tags,
  rewardId,
  price,
  purchaseLimit,
  coolDown,
}: {
  name: string;
  tags: string[];
  rewardId: string;
  price: ShopItemPriceInput;
  purchaseLimit: number;
  coolDown: CoolDown;
}) => {
  const response = await addShopItem({
    shopId: props.shopId,
    sectionId: props.section.id,
    name,
    rank: shopItems.value.length + 1,
    tags,
    rewardId,
    versionId,
    appId,
    price,
    purchaseLimit,
    coolDown,
  });

  if (response?.createShopItem) {
    closeAddShopItemModal();
  }
};

const handleEdit = () => {
  emits("edit", props.section.id);
};

const handleDuplicateItem = async (shopItemId: string) => {
  const itemToBeDuplicated = shopItems.value.find((item) => item.id === shopItemId);
  if (itemToBeDuplicated) {
    await duplicateShopItem(itemToBeDuplicated.id, props.section.id, props.shopId);
  }
};

const handleDelete = () => {
  emits("delete", props.section.id);
};

const handleChange = (event: SortChangeType<ShopItemType>): void => {
  if (event.added) {
    isDraggingDisabled.value = true;
    const shopItem = event.added.element;
    if (shopItem && shopItem.id) {
      emits("append-shop-item", {
        rank: event.added.newIndex + 1,
        shopItemId: shopItem.id,
        sectionId: props.section.id,
      });
    }
  }

  if (event.removed) {
    isDraggingDisabled.value = true;
    const shopItem = event.removed.element;
    if (shopItem && shopItem.id) {
      emits("remove-shop-item", {
        rank: event.removed.oldIndex + 1,
        shopItemId: shopItem.id,
        sectionId: props.section.id,
      });
    }
  }
};

const handleDragWithinSameSection = () => {
  for (const item of shopItems.value) {
    const index = shopItems.value.indexOf(item);
    if (item.rank !== index + 1) {
      isDraggingDisabled.value = true;
      emits("re-order-end", {
        newRank: index + 1,
        previousRank: item.rank,
        shopItemId: item.id,
        newShopSectionId: props.section.id,
        previousSectionId: props.section.id,
      });
      return;
    }
  }
};

type MoveEvent = {
  draggedContext: {
    element: ShopItemType;
  };
  relatedContext: {
    list: ShopItemType[];
  };
};
const shouldMove = (event: MoveEvent) => {
  const draggedItem = event.draggedContext.element;
  const targetList = event.relatedContext.list;
  const itemsWithSameTechnicalName = targetList.filter(
    (item) => item.id !== draggedItem.id && item.technicalName === draggedItem.technicalName
  );
  const hasDuplicateInTargetSection = itemsWithSameTechnicalName.length > 0;
  return !hasDuplicateInTargetSection;
};

const handleEditItem = (shopItemId: string) => {
  const itemToBeDeleted = shopItems.value.find((item) => item.id === shopItemId);
  if (itemToBeDeleted) {
    shopItemToBeEdited.value = itemToBeDeleted as unknown as ShopItemType;
    showAddShopItemModal();
  }
};

const handleDeleteItem = async (shopItemId: string) => {
  const {
    shopId,
    section: { id: shopSectionId },
  } = props;
  await deleteShopItem(shopItemId, shopSectionId, shopId);
};
</script>

<style module lang="scss">
.sectionHeadline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: var(--vd-color-neutral-light);
}
.sectionHeadlineText {
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--vd-color-text-default);
  margin-right: 0.5rem;
}
.cursorPointer {
  cursor: pointer;
}

.shopItemsInnerWrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  cursor: initial;
}

.shopItemsWrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem;
  cursor: initial;
}

.sectionId {
  font-size: 0.75rem;
  color: var(--vd-color-grey-light-1);
}

.copyId {
  margin-right: 0.75rem;
}
</style>
